import Button from '../Input2/Button';
import Popup from '../Popup/Popup';
import { Softphone, type CallOutcomeOption } from './Softphone';
import type { SoftphoneStatus } from './softphoneClient.types';

export interface ClickToCallButtonPureProps {
  /**
   * Team-level calling flag. When false the affordance is hidden entirely (returns null) —
   * the feature is gated, not greyed.
   */
  enabled: boolean;
  /**
   * Whether the rep can actually place a call (a caller number is provisioned). When the team
   * flag is on but this is false, the button renders greyed/disabled with an explanatory
   * tooltip rather than disappearing.
   */
  callingConnected: boolean;
  /** Contact's phone number (the value the UI shows); empty/null disables the button. */
  phoneNumber?: string | null;
  contactName?: string;

  /** Whether the dialer popup is open. */
  open: boolean;
  /** Start a call (open the dialer). */
  onCall: () => void;
  /** Close the dialer popup. */
  onClose: () => void;

  // Softphone passthrough (the smart wrapper owns this state via useSoftphone).
  status: SoftphoneStatus;
  durationSec: number;
  isMuted: boolean;
  error: string | null;
  isRecording?: boolean;
  outcomes: ReadonlyArray<CallOutcomeOption>;
  onToggleMute: () => void;
  onHangup: () => void;
  onSelectOutcome: (value: string) => void;

  className?: string;
}

/**
 * Pure, presentational click-to-call button + dialer popup. No tRPC, no softphone client — the
 * smart wrappers (dashboard `ClickToCallButton`, extension `ExtensionClickToCall`) own the
 * network + WebRTC and feed this their controller state. Keeping the view pure lets it be
 * component-tested without bundling the carrier SDKs.
 */
export function ClickToCallButtonPure({
  enabled,
  callingConnected,
  phoneNumber,
  contactName,
  open,
  onCall,
  onClose,
  status,
  durationSec,
  isMuted,
  error,
  isRecording,
  outcomes,
  onToggleMute,
  onHangup,
  onSelectOutcome,
  className,
}: ClickToCallButtonPureProps) {
  if (!enabled) return null;

  const hasPhone = Boolean(phoneNumber);
  // Precedence: an un-provisioned rep can't call any contact, so surface that first; otherwise
  // fall back to the per-contact "no phone" reason.
  const disabledReason = !callingConnected
    ? "Calling isn't set up for your account yet"
    : !hasPhone
      ? 'No phone number on file'
      : undefined;

  return (
    <>
      <Button
        color="green"
        disabled={disabledReason != null}
        tooltip={disabledReason}
        onClick={onCall}
        className={className}
      >
        Call
      </Button>

      {open && callingConnected && hasPhone && (
        <Popup onClose={onClose}>
          <Softphone
            contactName={contactName}
            phoneNumber={phoneNumber ?? ''}
            status={status}
            durationSec={durationSec}
            isMuted={isMuted}
            error={error}
            isRecording={isRecording}
            outcomes={outcomes}
            onToggleMute={onToggleMute}
            onHangup={onHangup}
            onSelectOutcome={onSelectOutcome}
            onClose={onClose}
          />
        </Popup>
      )}
    </>
  );
}
